'use client';

import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import { useState, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import { FlexColumn, FlexRow } from 'src/components/Atoms/FlexBox';
import { IconButton } from 'src/components/Atoms/IconButton';
import { Image } from 'src/components/Atoms/Image';
import useUserOnboarded from 'src/hooks/useUserOnboarded';
import Button from '@mui/material/Button';
import { styled, useTheme } from '@mui/material/styles';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { Theme } from 'src/Theme';
import useTranslations from 'src/hooks/useTranslations';
import useMediaQuery from '@mui/material/useMediaQuery';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
const WizardDialog = styled(FlexColumn)(({
  theme
}) => ({
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  padding: theme.sizes.spacing['08'],
  paddingTop: theme.sizes.spacing['11'],
  gap: theme.sizes.spacing['09'],
  borderRadius: theme.sizes.radius.lg,
  backgroundColor: theme.colors.surface.primary,
  boxShadow: theme.boxShadows[5]
}));
const ImageCarousel = styled(FlexColumn)(({
  theme
}) => ({
  width: '100%',
  maxWidth: '552px',
  gap: theme.sizes.spacing['07']
}));
const DotContainer = styled(FlexRow)(({
  theme
}) => ({
  alignSelf: 'center',
  margin: 0,
  gap: theme.sizes.spacing['02']
}));
const MessageContainer = styled(FlexColumn)(({
  theme
}) => ({
  alignItems: 'flex-start',
  gap: theme.sizes.spacing['06'],
  minHeight: '11rem'
}));
const ActionContainer = styled(FlexRow)(({
  theme
}) => ({
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  gap: theme.sizes.spacing['07']
}));
const BackButton = styled(Button)(({
  theme
}: {
  theme: Theme;
}) => ({
  backgroundColor: theme.colors.button['tonal-surface'],
  color: theme.colors.icon.primary,
  border: '1px solid transparent',
  '&:disabled': {
    backgroundColor: theme.colors.button.disabled,
    color: theme.colors.icon.disabled,
    border: '1px solid transparent'
  },
  '&:hover': {
    backgroundColor: theme.colors.button['tonal-surface'],
    border: `1px solid ${theme.colors.button.border}`
  },
  '&:focus': {
    backgroundColor: theme.colors.button['tonal-surface'],
    border: `1px solid ${theme.colors.border.focus}`,
    outline: 'none'
  }
}));
const IconContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'backgroundColor' && prop !== 'fillColor'
})<{
  backgroundColor: string;
  fillColor: string;
}>(({
  theme,
  backgroundColor,
  fillColor
}) => ({
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.sizes.radius.lg,
  backgroundColor: backgroundColor,
  width: '100%',
  aspectRatio: '16 / 9',
  maxWidth: '552px',
  maxHeight: '330px',
  alignSelf: 'center',
  '& svg': {
    fill: fillColor,
    width: '72px',
    height: '72px'
  }
}));
const MediaContainer = styled(Box)(({
  theme
}) => ({
  width: '100%',
  position: 'relative',
  maxWidth: '552px',
  maxHeight: '330px',
  alignSelf: 'center',
  aspectRatio: '16 / 9',
  '& video': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: theme.sizes.radius.lg
  }
}));
const CloseButton = styled(IconButton)(({
  theme
}) => ({
  position: 'absolute',
  right: 0,
  top: 0,
  padding: theme.sizes.spacing['08'],
  zIndex: 1000,
  color: theme.colors.text.primary,
  backgroundColor: 'none',
  '&:hover': {
    color: theme.colors.text.primary
  }
}));
interface WizardStep {
  title: string;
  message: string;
  image: string;
  videoUrl?: string;
  icon?: React.ReactNode;
}
const Wizard: React.FC = () => {
  const $ = _c(109);
  const {
    isUserOnboarded,
    markUserAsOnboarded
  } = useUserOnboarded();
  const [wizardOpen, setWizardOpen] = useState(!isUserOnboarded);
  const [currentStep, setCurrentStep] = useState(0);
  const theme = useTheme();
  const {
    t
  } = useTranslations();
  let t0;
  if ($[0] !== theme.breakpoints) {
    t0 = theme.breakpoints.down("md");
    $[0] = theme.breakpoints;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      noSsr: true
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isSmallScreen = useMediaQuery(t0, t1);
  const backgroundColor = theme.colors.icon["forest-background"];
  const fillColor = theme.colors.icon["forest-fill"];
  let t2;
  if ($[3] !== markUserAsOnboarded) {
    t2 = () => {
      setWizardOpen(false);
      markUserAsOnboarded();
    };
    $[3] = markUserAsOnboarded;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleClose = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      title: "Effektify is optimized for a desktop experience",
      message: "For the best experience, please continue on your computer.",
      image: "",
      icon: <ComputerOutlinedIcon width={72} height={72} />
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const smallScreenStep = t3;
  let t4;
  if ($[6] !== t) {
    t4 = t("components.wizard.title1");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("components.wizard.description1");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = {
      title: t4,
      message: t5,
      videoUrl: "https://effektify-production-public.s3.eu-north-1.amazonaws.com/ai_avatar_effektify_one.mp4",
      image: "/images/growth_hub.webp"
    };
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("components.wizard.title2");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t("components.wizard.description2");
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = {
      title: t7,
      message: t8,
      image: "/images/next_move_AI.webp"
    };
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t) {
    t10 = t("components.wizard.title3");
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t) {
    t11 = t("components.wizard.description3");
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = {
      title: t10,
      message: t11,
      image: "/images/know_whats_happening.webp"
    };
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t) {
    t13 = t("components.wizard.title4");
    $[27] = t;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t) {
    t14 = t("components.wizard.description4");
    $[29] = t;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t13 || $[32] !== t14) {
    t15 = {
      title: t13,
      message: t14,
      image: "/images/real_experts.webp"
    };
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t12 || $[35] !== t15 || $[36] !== t6 || $[37] !== t9) {
    t16 = [t6, t9, t12, t15];
    $[34] = t12;
    $[35] = t15;
    $[36] = t6;
    $[37] = t9;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const value = t16;
  let t17;
  if ($[39] !== isSmallScreen || $[40] !== value) {
    t17 = isSmallScreen ? [smallScreenStep, ...value] : value;
    $[39] = isSmallScreen;
    $[40] = value;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const steps = t17;
  const step = steps[currentStep];
  const t18 = step?.message || "";
  const t19 = step?.title || "";
  const t20 = step?.image || "";
  const t21 = step?.videoUrl;
  const t22 = step?.icon;
  let t23;
  if ($[42] !== t18 || $[43] !== t19 || $[44] !== t20 || $[45] !== t21 || $[46] !== t22) {
    t23 = {
      message: t18,
      title: t19,
      image: t20,
      videoUrl: t21,
      icon: t22
    };
    $[42] = t18;
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  const {
    message,
    title,
    image,
    videoUrl,
    icon
  } = t23;
  let t24;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      zIndex: 3000
    };
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <CloseIcon />;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== handleClose) {
    t26 = <CloseButton onClick={handleClose}>{t25}</CloseButton>;
    $[50] = handleClose;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] !== image || $[53] !== t || $[54] !== title || $[55] !== videoUrl) {
    t27 = videoUrl && <video controls={true} title={t("components.wizard.videoTitle")}><source src={videoUrl} type="video/mp4" /><Image src={image} alt={title} width={552} height={330} /></video>;
    $[52] = image;
    $[53] = t;
    $[54] = title;
    $[55] = videoUrl;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== backgroundColor || $[58] !== fillColor || $[59] !== icon || $[60] !== videoUrl) {
    t28 = !videoUrl && icon ? <IconContainer backgroundColor={backgroundColor} fillColor={fillColor}>{icon}</IconContainer> : null;
    $[57] = backgroundColor;
    $[58] = fillColor;
    $[59] = icon;
    $[60] = videoUrl;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== icon || $[63] !== image || $[64] !== title || $[65] !== videoUrl) {
    t29 = !videoUrl && !icon && <Image src={image} alt={title} fill={true} />;
    $[62] = icon;
    $[63] = image;
    $[64] = title;
    $[65] = videoUrl;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t27 || $[68] !== t28 || $[69] !== t29) {
    t30 = <MediaContainer>{t27}{t28}{t29}</MediaContainer>;
    $[67] = t27;
    $[68] = t28;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== currentStep || $[72] !== steps || $[73] !== theme.colors.icon.primary || $[74] !== theme.colors.surface) {
    t31 = <DotContainer>{steps.map((step_0, index) => <svg key={step_0.title} width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="5" cy="5" r="5" fill={index === currentStep ? theme.colors.icon.primary : theme.colors.surface.tertiary} /></svg>)}</DotContainer>;
    $[71] = currentStep;
    $[72] = steps;
    $[73] = theme.colors.icon.primary;
    $[74] = theme.colors.surface;
    $[75] = t31;
  } else {
    t31 = $[75];
  }
  let t32;
  if ($[76] !== t30 || $[77] !== t31) {
    t32 = <ImageCarousel>{t30}{t31}</ImageCarousel>;
    $[76] = t30;
    $[77] = t31;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  let t33;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      flexGrow: 0
    };
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] !== title) {
    t34 = <Typography variant="heading-xl" component="h2" sx={t33}>{title}</Typography>;
    $[80] = title;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  if ($[82] !== message) {
    t35 = <Typography variant="body-base" component="p">{message}</Typography>;
    $[82] = message;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== t34 || $[85] !== t35) {
    t36 = <MessageContainer>{t34}{t35}</MessageContainer>;
    $[84] = t34;
    $[85] = t35;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  let t37;
  if ($[87] !== currentStep) {
    t37 = () => setCurrentStep(currentStep - 1);
    $[87] = currentStep;
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  const t38 = currentStep === 0;
  let t39;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <ArrowBack />;
    $[89] = t39;
  } else {
    t39 = $[89];
  }
  let t40;
  if ($[90] !== t37 || $[91] !== t38) {
    t40 = <BackButton variant="outlined" onClick={t37} disabled={t38}>{t39}</BackButton>;
    $[90] = t37;
    $[91] = t38;
    $[92] = t40;
  } else {
    t40 = $[92];
  }
  let t41;
  if ($[93] !== currentStep || $[94] !== handleClose || $[95] !== steps.length || $[96] !== t) {
    t41 = currentStep < steps.length - 1 ? <><Button onClick={() => setCurrentStep(currentStep + 1)}>{t("components.wizard.next")}</Button><Button onClick={handleClose} sx={{
        marginLeft: "auto"
      }} variant="text">{t("components.wizard.tryNow")}</Button></> : <Button onClick={handleClose}>{t("components.wizard.getStarted")}</Button>;
    $[93] = currentStep;
    $[94] = handleClose;
    $[95] = steps.length;
    $[96] = t;
    $[97] = t41;
  } else {
    t41 = $[97];
  }
  let t42;
  if ($[98] !== t40 || $[99] !== t41) {
    t42 = <ActionContainer>{t40}{t41}</ActionContainer>;
    $[98] = t40;
    $[99] = t41;
    $[100] = t42;
  } else {
    t42 = $[100];
  }
  let t43;
  if ($[101] !== t26 || $[102] !== t32 || $[103] !== t36 || $[104] !== t42) {
    t43 = <WizardDialog>{t26}{t32}{t36}{t42}</WizardDialog>;
    $[101] = t26;
    $[102] = t32;
    $[103] = t36;
    $[104] = t42;
    $[105] = t43;
  } else {
    t43 = $[105];
  }
  let t44;
  if ($[106] !== t43 || $[107] !== wizardOpen) {
    t44 = <Dialog open={wizardOpen} sx={t24}>{t43}</Dialog>;
    $[106] = t43;
    $[107] = wizardOpen;
    $[108] = t44;
  } else {
    t44 = $[108];
  }
  return t44;
};
export default Wizard;